import React, { useState } from 'react';

export default function BookingForm({ formType = 'booking' }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    social_handle: '',
    service_needed: 'content-redigering',
    campaign_interest: 'indledende_samtale',
    message: '',
    website_url: '', // Honeypot antispam field - strictly hidden
  });

  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, submitted: false, error: null });

    // Client-side validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      setStatus({
        submitting: false,
        submitted: false,
        error: 'Udfyld venligst alle obligatoriske felter (Navn, Telefon og Email).',
      });
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          form_type: formType,
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          company: formData.company,
          social_handle: formData.social_handle,
          service_needed: formData.service_needed,
          campaign_interest: formData.campaign_interest,
          message: formData.message || 'Henvendelse vedrørende online SoMe & content-service.',
          website_url: formData.website_url, // Honeypot
        }),
      });

      if (!response.ok) {
        throw new Error('Noget gik galt ved indsendelse. Prøv venligst igen eller ring direkte.');
      }

      setStatus({ submitting: false, submitted: true, error: null });
    } catch (err) {
      console.error('Form submit error:', err);
      setStatus({
        submitting: false,
        submitted: false,
        error: 'Der opstod en fejl ved afsendelse. Prøv venligst igen om et øjeblik.',
      });
    }
  };

  if (status.submitted) {
    return (
      <div className="bg-[#FAF8F5] border border-[#E5DFD6] rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-sm">
        <div className="w-16 h-16 rounded-full bg-[#161514] text-[#C5A880] mx-auto flex items-center justify-center text-2xl">
          <i className="fa-light fa-check" aria-hidden="true" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-[#161514] tracking-tight">
            Mange tak for din henvendelse!
          </h3>
          <p className="text-base text-[#5C5852] max-w-md mx-auto leading-relaxed">
            Vi har modtaget dine oplysninger og vender tilbage inden for 24 timer for en uforpligtende snak om dine muligheder og behov.
          </p>
        </div>
        <div className="pt-4 border-t border-[#EAE4DC] text-xs text-[#8C867D]">
          Vil du i kontakt med det samme? Ring på{' '}
          <a href="tel:+4561489206" className="no-break font-semibold text-[#161514] underline">
            +45 61 48 92 06
          </a>
        </div>
      </div>
    );
  }

  return (
    <form
      id="kontaktformular"
      onSubmit={handleSubmit}
      className="bg-white border border-[#E5DFD6] rounded-3xl p-6 sm:p-10 shadow-sm space-y-6 scroll-mt-28"
      noValidate
    >
      {/* Honeypot field - strictly hidden from humans */}
      <div className="sr-only absolute -left-[9999px] -top-[9999px]" aria-hidden="true">
        <label htmlFor="website_url">Website URL (leave blank)</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          value={formData.website_url}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="px-2.5 py-0.5 rounded-full bg-[#FAF0E6] text-[#A8885B] font-bold text-xs uppercase tracking-wider">
            Uforpligtende kontakt
          </span>
          <span className="text-xs text-[#8C867D]">· 0 binding</span>
        </div>
        <h3 className="text-2xl font-bold text-[#161514] tracking-tight">
          {formType === 'booking' ? 'Få en uforpligtende aftale' : 'Start en uforpligtende dialog'}
        </h3>
        <p className="text-sm text-[#5C5852]">
          Uanset om du driver hotel, Airbnb, restaurant, butik eller servicevirksomhed, sammensætter vi en individuel løsning uden faste prispakker. Udfyld felterne nedenfor, så kontakter vi dig hurtigt.
        </p>
      </div>

      {status.error && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm flex items-start gap-3">
          <i className="fa-regular fa-circle-exclamation text-base mt-0.5" aria-hidden="true" />
          <span>{status.error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {/* Navn */}
        <div className="space-y-1.5">
          <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-[#4A4640]">
            Navn <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="Fx Sarah Jensen"
            className="w-full px-4 py-3 rounded-xl bg-white border border-[#DDD6CC] text-[#161514] placeholder-[#A39C93] text-sm focus:outline-none focus:ring-2 focus:ring-[#161514] focus:border-transparent transition-all"
          />
        </div>

        {/* Virksomhed */}
        <div className="space-y-1.5">
          <label htmlFor="company" className="block text-xs font-semibold uppercase tracking-wider text-[#4A4640]">
            Virksomhed / Forretning
          </label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            placeholder="Fx Strandly Hotel, Klitly Airbnb eller Café Luna"
            className="w-full px-4 py-3 rounded-xl bg-white border border-[#DDD6CC] text-[#161514] placeholder-[#A39C93] text-sm focus:outline-none focus:ring-2 focus:ring-[#161514] focus:border-transparent transition-all"
          />
        </div>

        {/* Telefon */}
        <div className="space-y-1.5">
          <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-[#4A4640]">
            Telefonnummer <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            required
            value={formData.phone}
            onChange={handleChange}
            placeholder="Fx 20 30 40 50"
            className="w-full px-4 py-3 rounded-xl bg-white border border-[#DDD6CC] text-[#161514] placeholder-[#A39C93] text-sm focus:outline-none focus:ring-2 focus:ring-[#161514] focus:border-transparent transition-all"
          />
        </div>

        {/* Email */}
        <div className="space-y-1.5">
          <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-[#4A4640]">
            Emailadresse <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            placeholder="kontakt@din-forretning.dk"
            className="w-full px-4 py-3 rounded-xl bg-white border border-[#DDD6CC] text-[#161514] placeholder-[#A39C93] text-sm focus:outline-none focus:ring-2 focus:ring-[#161514] focus:border-transparent transition-all"
          />
        </div>
      </div>

      {/* SoMe profil / link */}
      <div className="space-y-1.5">
        <label htmlFor="social_handle" className="block text-xs font-semibold uppercase tracking-wider text-[#4A4640]">
          Nuværende Instagram / Facebook / TikTok (valgfrit)
        </label>
        <input
          type="text"
          id="social_handle"
          name="social_handle"
          value={formData.social_handle}
          onChange={handleChange}
          placeholder="Fx @cafeluna_cph eller link til profil"
          className="w-full px-4 py-3 rounded-xl bg-white border border-[#DDD6CC] text-[#161514] placeholder-[#A39C93] text-sm focus:outline-none focus:ring-2 focus:ring-[#161514] focus:border-transparent transition-all"
        />
      </div>

      {/* Primær service & Kampagne */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-1.5">
          <label htmlFor="service_needed" className="block text-xs font-semibold uppercase tracking-wider text-[#4A4640]">
            Hvad søger I primært hjælp til?
          </label>
          <select
            id="service_needed"
            name="service_needed"
            value={formData.service_needed}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl bg-white border border-[#DDD6CC] text-[#161514] text-sm focus:outline-none focus:ring-2 focus:ring-[#161514] focus:border-transparent transition-all"
          >
            <option value="content-redigering">Content Opsætning & Redigering (vi klipper jeres råklip)</option>
            <option value="online-vejledning">Online Vejledning (tips til foto & video)</option>
            <option value="meta-postkalender">Meta SoMe Postkalender & Planlægning</option>
            <option value="profil-opsaetning">Opsætning af Sociale Medier (profil/bio)</option>
            <option value="samlet-aftale">Kombineret løsning (redigering + kalender)</option>
            <option value="andet">Andet / Lad os drøfte mulighederne</option>
          </select>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="campaign_interest" className="block text-xs font-semibold uppercase tracking-wider text-[#4A4640]">
            Ønske til forløb & opstart
          </label>
          <select
            id="campaign_interest"
            name="campaign_interest"
            value={formData.campaign_interest}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl bg-white border border-[#DDD6CC] text-[#161514] text-sm focus:outline-none focus:ring-2 focus:ring-[#161514] focus:border-transparent transition-all font-medium"
          >
            <option value="indledende_samtale">Uforpligtende indledende samtale (afklar muligheder & behov)</option>
            <option value="ja_2_for_1">Særtilbud: Betal for 2 mdr – få 1 mdr gratis (0 binding)</option>
            <option value="tilpasset_aftale">Skræddersyet forløb ud fra vores ønsker</option>
          </select>
        </div>
      </div>

      {/* Beskrivelse */}
      <div className="space-y-1.5">
        <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wider text-[#4A4640]">
          Kort om jeres ønsker og forretning
        </label>
        <textarea
          id="message"
          name="message"
          rows={3}
          value={formData.message}
          onChange={handleChange}
          placeholder="Fortæl kort om hvad jeres virksomhed laver, og hvor meget indhold I gerne vil udgive..."
          className="w-full px-4 py-3 rounded-xl bg-white border border-[#DDD6CC] text-[#161514] placeholder-[#A39C93] text-sm focus:outline-none focus:ring-2 focus:ring-[#161514] focus:border-transparent transition-all resize-y"
        />
      </div>

      <div className="pt-2">
        <button
          type="submit"
          disabled={status.submitting}
          className="w-full inline-flex items-center justify-center gap-2 py-4 px-8 rounded-full bg-[#161514] text-[#FAF8F5] font-semibold text-base hover:bg-[#2A2926] active:scale-99 transition-all cursor-pointer disabled:opacity-50 shadow-md"
        >
          {status.submitting ? (
            <>
              <i className="fa-solid fa-spinner-third animate-spin text-sm" aria-hidden="true" />
              <span>Sender henvendelse...</span>
            </>
          ) : (
            <>
              <span>Send uforpligtende henvendelse</span>
              <i className="fa-regular fa-arrow-right text-sm" aria-hidden="true" />
            </>
          )}
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-[#7A746B] text-center pt-1">
        <span className="flex items-center gap-1.5">
          <i className="fa-light fa-shield-check text-[#C5A880]" aria-hidden="true" />
          <span>0 binding</span>
        </span>
        <span>•</span>
        <span className="flex items-center gap-1.5">
          <i className="fa-light fa-sparkles text-[#C5A880]" aria-hidden="true" />
          <span>Sparring & rådgivning</span>
        </span>
        <span>•</span>
        <span className="flex items-center gap-1.5">
          <i className="fa-light fa-clock text-[#C5A880]" aria-hidden="true" />
          <span>Svar inden for 24 timer</span>
        </span>
      </div>
    </form>
  );
}
